import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { useAuth } from '@leagueplatform/auth-utils';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import {
  refreshCaregiverStatus,
  useCaregiverStatus,
  useIsCaregiver,
  useIsCaregiverShell,
} from 'hooks/use-is-caregiver.hook';
import { CaregiverStatusError } from 'components/caregiver-status-error/caregiver-status-error.component';

// Guard for member-only routes. A non-impersonating caregiver ("caregiver
// shell") is confined to the caregiver home, so we redirect them there;
// every other authenticated user renders the requested member route via
// <Outlet />. While the caregiver claim is still resolving we render a loading
// indicator rather than the member route, so a caregiver never briefly sees
// member content (home, benefits, journey, etc.) before the redirect kicks in.
export const RequireMemberContainer = () => {
  const status = useCaregiverStatus();
  const isCaregiverShell = useIsCaregiverShell();

  if (status === 'error') return <CaregiverStatusError />;

  if (isCaregiverShell === null) return <LoadingIndicator />;

  if (isCaregiverShell) {
    return <Navigate to={APP_MANAGED_ROUTES.CAREGIVER_HOME} replace />;
  }

  return <Outlet />;
};

// Guard for the caregiver home, which mounts a caregiver-only Masonry node.
// Only a caregiver (impersonating or at-rest) may reach it; any other member is
// redirected home rather than rendering the caregiver-only node in an error /
// empty state. We wait for the caregiver claim to resolve before deciding.
export const RequireCaregiver = () => {
  const status = useCaregiverStatus();
  const isCaregiver = useIsCaregiver();

  if (status === 'error') return <CaregiverStatusError />;

  if (isCaregiver === null) return <LoadingIndicator />;

  if (!isCaregiver) {
    return <Navigate to={APP_MANAGED_ROUTES.HOME} replace />;
  }

  return <Outlet />;
};

// Pre-auth gate for the caregiver account-claim deep link
// (`/caregiver/claim-account?token=…`). A caregiver arriving from their
// invitation email is signed out, so this route redirects to `/sign-in`
// preserving the original deep link as `redirect_uri`, and the claim flow
// resumes after authentication. An already-authenticated user renders the claim
// flow via <Outlet />.
//
// The redirect names no connection: caregivers sign in through the same one as
// members, which is the sign-in page's default.
// On the way out of the claim flow the cached caregiver status is discarded, so
// whatever the claim just changed decides the caregiver experience rather than
// whatever was true before it. This matters for someone already using the member
// app in the same tab when they click their invitation: their status was read and
// cached as "member" before the relationship existed, and without this they would
// complete the claim and still be treated as a member until a page reload. The
// refresh is skipped for a visitor who never got past the sign-in redirect, since
// there is nothing to re-ask and an unauthenticated request would only log noise.
export const RequireCaregiverClaimAuth = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const renderedClaimFlow = useRef(false);

  useEffect(() => {
    if (isAuthenticated) renderedClaimFlow.current = true;
  }, [isAuthenticated]);

  useEffect(
    () => () => {
      if (renderedClaimFlow.current) refreshCaregiverStatus();
    },
    [],
  );

  if (isAuthenticated === undefined) return <LoadingIndicator />;

  if (!isAuthenticated) {
    const params = new URLSearchParams();
    params.set('redirect_uri', location.pathname + location.search);
    return <Navigate to={`${APP_MANAGED_ROUTES.SIGN_IN}?${params}`} replace />;
  }

  return <Outlet />;
};
